import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileCode2, Loader2, Save } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const LEDGERS = [
  ["purchase_ledger", "Fabric purchase ledger"],
  ["jobwork_ledger", "Job work charges ledger"],
  ["cgst_ledger", "Input CGST ledger"],
  ["sgst_ledger", "Input SGST ledger"],
  ["igst_ledger", "Input IGST ledger"],
  ["party_group", "Group for vendors / fabricators"],
  ["purchase_vtype", "Voucher type"],
];

export const TallyTab = () => {
  const { isAdmin } = useAuth();
  const [t, setT] = useState(null);
  const [f, setF] = useState({ from_date: "", to_date: "", include_grns: true, include_job_bills: true, only_new: true, include_ledgers: true });
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/tally-settings").then((r) => setT(r.data));
  }, []);
  if (!t) return <p className="text-muted-foreground">Loading…</p>;

  const saveLedgers = async () => {
    setBusy("save");
    try {
      setT((await api.put("/tally-settings", t)).data);
      toast.success("Tally ledger names saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const resetFlags = async () => {
    const period = f.from_date || f.to_date ? "the selected period" : "ALL dates";
    if (!window.confirm(`Allow re-export of entries for ${period}? They will be included again in the next 'only new' export.`)) return;
    setBusy("reset");
    try {
      const { data } = await api.post("/export/tally/reset", { ...f, from_date: f.from_date || null, to_date: f.to_date || null });
      toast.success(`${data.reset} ${data.reset === 1 ? "entry" : "entries"} can be exported again`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const exportXml = async () => {
    setBusy("export");
    try {
      const r = await api.post("/export/tally", { ...f, from_date: f.from_date || null, to_date: f.to_date || null }, { responseType: "blob" });
      const href = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = href;
      a.download = `tally-import-${new Date().toISOString().slice(0, 10)}.xml`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(href), 4000);
      const n = Number(r.headers["x-voucher-count"] || 0);
      toast.success(`${n} voucher${n === 1 ? "" : "s"} exported`);
    } catch (e) {
      const text = e.response?.data instanceof Blob ? await e.response.data.text() : "";
      toast.error(text ? JSON.parse(text).detail : errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const check = (k, label) => (
    <label className="flex min-h-[44px] items-center gap-3 text-sm font-medium">
      <input type="checkbox" className="h-5 w-5" checked={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`tally-${k}`} /> {label}
    </label>
  );

  return (
    <div className="space-y-3" data-testid="tally-tab">
      <div className="panel space-y-3 p-4">
        <h3 className="font-heading font-bold">Export to Tally</h3>
        <p className="text-sm text-muted-foreground">Creates a Tally XML file with purchase vouchers for GRNs (received value + GST) and fabricator job-work bills. In Tally: Gateway → Import → Vouchers, choose this file.</p>
        <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2">
          <div><label className="label">From date</label><input type="date" className="field" value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value })} data-testid="tally-from-date" /></div>
          <div><label className="label">To date</label><input type="date" className="field" value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} data-testid="tally-to-date" /></div>
        </div>
        {check("include_grns", "GRN purchases")}
        {check("include_job_bills", "Fabricator job-work bills")}
        {check("only_new", "Only entries not exported before")}
        {check("include_ledgers", "Also create vendor / fabricator ledgers")}
        <button className="btn btn-primary w-full" onClick={exportXml} disabled={!!busy || (!f.include_grns && !f.include_job_bills)} data-testid="tally-export-btn">
          {busy === "export" ? <Loader2 size={18} className="animate-spin" /> : <FileCode2 size={18} />} Download Tally file
        </button>
        <p className="text-xs text-muted-foreground">CGST + SGST are used when the vendor's GSTIN state matches your company GSTIN (Karnataka 29 if not set); otherwise IGST. With "create ledgers" ticked, the purchase, job work and GST ledgers below are also created if missing.</p>
        {isAdmin && (
          <button className="btn btn-outline w-full" disabled={!!busy} onClick={resetFlags} data-testid="tally-reset-btn">
            {busy === "reset" && <Loader2 size={18} className="animate-spin" />} Allow re-export for this period
          </button>
        )}
      </div>
      <div className="panel space-y-3 p-4">
        <h3 className="font-heading font-bold">Ledger names in your Tally</h3>
        <p className="text-xs text-muted-foreground">These must match the ledger names your accountant uses in Tally exactly.</p>
        {LEDGERS.map(([k, label]) => (
          <div key={k}>
            <label className="label">{label}</label>
            <input className="field" value={t[k]} disabled={!isAdmin} onChange={(e) => setT({ ...t, [k]: e.target.value })} data-testid={`tally-${k}-input`} />
          </div>
        ))}
        {isAdmin && (
          <button className="btn btn-outline w-full" onClick={saveLedgers} disabled={!!busy} data-testid="tally-save-ledgers-btn">
            {busy === "save" ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Save ledger names
          </button>
        )}
      </div>
    </div>
  );
};
